/**
 * Analytics tracking utilities for NFTotem using Vercel Analytics
 * Tracks user interactions with minting and editing functionality
 */

import { track } from '@vercel/analytics'

// Analytics event types for Vercel Analytics
export type AnalyticsEventType = 
  | 'mint_paid'           // Paid minting
  | 'mint_free'           // Free minting
  | 'edit_totem'          // Totem editing
  | 'mint_attempt'        // Mint attempt (before success/failure)
  | 'edit_attempt'        // Edit attempt (before success/failure)
  | 'mint_error'          // Mint error
  | 'edit_error'          // Edit error

// Analytics event properties for Vercel Analytics
export interface AnalyticsEvent {
  event: AnalyticsEventType
  wallet_type: 'metamask' | 'walletconnect'
  price?: string                    // ETH price for paid mints
  is_free_mint?: boolean           // Whether it's a free mint
  collections_count?: number       // Number of collections
  token_id?: number               // Token ID for edits
  error_message?: string          // Error message for error events
}

// Analytics class using Vercel Analytics
class Analytics {
  private enabled: boolean
  private debug: boolean

  constructor() {
    this.enabled = process.env.NODE_ENV === 'production'
    this.debug = process.env.NODE_ENV === 'development'
  }

  /**
   * Track an analytics event using Vercel Analytics
   * @param event Analytics event to track
   */
  async track(event: AnalyticsEvent): Promise<void> {
    if (!this.enabled) {
      if (this.debug) {
        console.log('[Vercel Analytics] Event not tracked (disabled):', event)
      }
      return
    }

    try {
      if (this.debug) {
        console.log('[Vercel Analytics] Tracking event:', event)
      }

      // Send to Vercel Analytics
      await this.sendToVercelAnalytics(event)
    } catch (error) {
      console.error('[Vercel Analytics] Failed to track event:', error)
    }
  }

  /**
   * Send event to Vercel Analytics
   * @param event Analytics event
   */
  private async sendToVercelAnalytics(event: AnalyticsEvent): Promise<void> {
    try {
      // Vercel Analytics track function
      track(event.event, {
        wallet_type: event.wallet_type,
        price: event.price,
        is_free_mint: event.is_free_mint,
        collections_count: event.collections_count,
        token_id: event.token_id,
        error_message: event.error_message,
      })
    } catch (error) {
      console.error('[Vercel Analytics] Failed to send event:', error)
    }
  }

  /**
   * Track mint attempt (before success/failure)
   * @param walletType Wallet type (metamask, walletconnect)
   * @param price ETH price
   * @param isFreeMint Whether it's a free mint
   * @param collectionsCount Number of collections
   */
  async trackMintAttempt(
    walletType: 'metamask' | 'walletconnect',
    price: string,
    isFreeMint: boolean,
    collectionsCount: number
  ): Promise<void> {
    await this.track({
      event: 'mint_attempt',
      wallet_type: walletType,
      price: isFreeMint ? '0' : price,
      is_free_mint: isFreeMint,
      collections_count: collectionsCount,
    })
  }

  /**
   * Track successful paid mint
   * @param walletType Wallet type
   * @param price ETH price
   * @param collectionsCount Number of collections
   * @param tokenId Minted token ID
   */
  async trackPaidMint(
    walletType: 'metamask' | 'walletconnect',
    price: string,
    collectionsCount: number,
    tokenId: number
  ): Promise<void> {
    await this.track({
      event: 'mint_paid',
      wallet_type: walletType,
      price,
      is_free_mint: false,
      collections_count: collectionsCount,
      token_id: tokenId,
    })
  }

  /**
   * Track successful free mint
   * @param walletType Wallet type
   * @param collectionsCount Number of collections
   * @param tokenId Minted token ID
   */
  async trackFreeMint(
    walletType: 'metamask' | 'walletconnect',
    collectionsCount: number,
    tokenId: number
  ): Promise<void> {
    await this.track({
      event: 'mint_free',
      wallet_type: walletType,
      price: '0',
      is_free_mint: true,
      collections_count: collectionsCount,
      token_id: tokenId,
    })
  }

  /**
   * Track edit attempt (before success/failure)
   * @param walletType Wallet type
   * @param tokenId Token ID being edited
   * @param collectionsCount Number of collections
   */
  async trackEditAttempt(
    walletType: 'metamask' | 'walletconnect',
    tokenId: number,
    collectionsCount: number
  ): Promise<void> {
    await this.track({
      event: 'edit_attempt',
      wallet_type: walletType,
      token_id: tokenId,
      collections_count: collectionsCount,
    })
  }

  /**
   * Track successful edit
   * @param walletType Wallet type
   * @param tokenId Token ID that was edited
   * @param collectionsCount Number of collections
   */
  async trackEditTotem(
    walletType: 'metamask' | 'walletconnect',
    tokenId: number,
    collectionsCount: number
  ): Promise<void> {
    await this.track({
      event: 'edit_totem',
      wallet_type: walletType,
      token_id: tokenId,
      collections_count: collectionsCount,
    })
  }

  /**
   * Track mint error
   * @param walletType Wallet type
   * @param errorMessage Error message
   * @param price ETH price (if applicable)
   * @param isFreeMint Whether it was a free mint attempt
   */
  async trackMintError(
    walletType: 'metamask' | 'walletconnect',
    errorMessage: string,
    price?: string,
    isFreeMint?: boolean
  ): Promise<void> {
    await this.track({
      event: 'mint_error',
      wallet_type: walletType,
      price: isFreeMint ? '0' : price,
      is_free_mint: isFreeMint,
      error_message: errorMessage,
    })
  }

  /**
   * Track edit error
   * @param walletType Wallet type
   * @param errorMessage Error message
   * @param tokenId Token ID (if applicable)
   */
  async trackEditError(
    walletType: 'metamask' | 'walletconnect',
    errorMessage: string,
    tokenId?: number
  ): Promise<void> {
    await this.track({
      event: 'edit_error',
      wallet_type: walletType,
      token_id: tokenId,
      error_message: errorMessage,
    })
  }
}

// Create and export analytics instance
export const analytics = new Analytics()

// Export types and class for custom usage
export { Analytics }
